/**
 * Auto-refresh helpers for ApiService
 *
 * Extracted from api.service.ts to keep that file ≤500 lines per LLM-friendly
 * architecture rules. Provides:
 *  - `isAuthEndpointPathThatMustNotTriggerRefresh()` — recursion guard.
 *  - `performAccessTokenRefreshAgainstBackend()` — single fetch to /auth/refresh.
 *  - `clearAuthDataLocally()` — wipe storage + memory token without circular
 *    importing the auth service.
 *
 * The refresh token is stored as an httpOnly cookie by the backend, so we MUST
 * pass `credentials: 'include'` for the browser to attach it.
 */

import { storageService } from '@/shared/services/storage.service'
import { STORAGE_KEYS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

/**
 * Endpoints that must NEVER trigger the auto-refresh interceptor.
 *
 * - `/auth/refresh`: refreshing while refreshing = infinite loop.
 * - `/auth/login`:   401 here is "wrong credentials", not an expired session.
 * - `/auth/logout`:  401 here doesn't matter; we're already tearing the
 *                    session down.
 */
const AUTH_ENDPOINTS_THAT_BYPASS_REFRESH = [
  '/api/auth/refresh',
  '/api/auth/login',
  '/api/auth/logout',
] as const

/**
 * Returns true if `endpoint` (relative path, e.g. `/api/auth/refresh?foo=1`)
 * matches one of the auth endpoints we must NOT auto-refresh on.
 *
 * Compares the path portion only — query strings and a single trailing slash
 * are ignored. Uses EXACT match (not `endsWith`) so a request to
 * `/api/some/sneaky-path/api/auth/refresh` does NOT match.
 */
export function isAuthEndpointPathThatMustNotTriggerRefresh(endpoint: string): boolean {
  // Strip query string and trailing slash before matching so
  // `/api/auth/refresh?x=1` and `/api/auth/refresh/` are both recognized.
  const pathOnly = (endpoint.split('?')[0] ?? endpoint).replace(/\/$/, '')
  return AUTH_ENDPOINTS_THAT_BYPASS_REFRESH.some(
    (authPath) => pathOnly === authPath || pathOnly === authPath.replace(/\/$/, ''),
  )
}

/**
 * 401 codes that say "THIS CREDENTIAL WAS REJECTED", not "your session token expired".
 *
 * The interceptor treats a 401 as an expired access token: it refreshes the session and
 * re-sends the request once. For a password (or code, or passkey) the server has just
 * refused that only doubles the attempt — every failed try counts twice toward a limit or
 * lockout — rotates the refresh token for nothing and, if the refresh then failed, would
 * announce "session expired" to someone who merely mistyped a password. Measured: one click
 * on "Disable 2FA" with a wrong password sent `DELETE /auth/mfa/disable` twice.
 *
 * ⚠️ Deliberately a DENY-list of credential rejections, not an allow-list of token codes:
 * a 401 with any other (or no) code keeps the previous behaviour, so a token failure the
 * list does not know about still refreshes instead of surfacing as an error.
 */
const REJECTED_CREDENTIAL_CODES: ReadonlySet<string> = new Set([
  'INVALID_PASSWORD',
  'INVALID_CREDENTIALS',
  'INVALID_MFA_CODE',
  'INVALID_CHALLENGE',
  'MFA_PRE_AUTH_REQUIRED',
  'PASSKEY_AUTH_FAILED',
  'PASSKEY_VERIFICATION_FAILED',
  'PASSKEY_NOT_FOUND',
])

/** Does this 401 body carry a rejected-credential code (so refreshing cannot help)? */
export function isRejectedCredentialResponse(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false
  const error = (body as { error?: unknown }).error
  if (typeof error !== 'object' || error === null) return false
  const code = (error as { code?: unknown }).code
  return typeof code === 'string' && REJECTED_CREDENTIAL_CODES.has(code)
}

/**
 * Shape of the backend `/auth/refresh` success response that we depend on.
 * Refresh response wraps the new access token under `data.tokens.accessToken`
 * (matches the LoginResponse shape used by login + MFA challenge).
 */
interface RefreshSuccessResponseBody {
  success?: boolean
  data?: {
    tokens?: {
      accessToken?: string
    }
  }
}

/**
 * Outcome of one `POST /api/auth/refresh`.
 *
 *  - `ok`:        a new access token (already persisted to storage).
 *  - `rejected`:  the server REFUSED the credential (400/401/403/404, or a 200 without a token): the session is dead.
 *  - `transient`: the server could not renew it NOW (408, 429, 5xx, network or timeout, any other status): the session may well
 *                 be alive, so nothing may be torn down on this evidence. `retryAfterSeconds` carries the
 *                 server's `Retry-After` when it sent a valid one.
 */
export type RefreshResult =
  | { kind: 'ok'; accessToken: string }
  | { kind: 'rejected' }
  | { kind: 'transient'; retryAfterSeconds?: number }

/** A refresh that hangs would block the single flight and every waiter; past this it counts as `transient`. */
const REFRESH_FETCH_TIMEOUT_MS = 15_000

const REJECTED_REFRESH_STATUSES: ReadonlySet<number> = new Set([400, 401, 403, 404])

/** `Retry-After` is either whole seconds or an HTTP date; anything else is "not sent". */
function parseRetryAfterSeconds(header: string | null): number | undefined {
  if (header === null) return undefined
  const trimmed = header.trim()
  if (/^\d+$/.test(trimmed)) return Number(trimmed)
  const dateMs = Date.parse(trimmed)
  if (Number.isNaN(dateMs)) return undefined
  return Math.max(0, Math.ceil((dateMs - Date.now()) / 1000))
}

/**
 * Calls `POST /api/auth/refresh` once and classifies the answer as a {@link RefreshResult}.
 *
 * Does NOT throw — callers decide what each kind means (only `rejected` ends the session).
 *
 * SIDE EFFECT: on success, persists the new access token to storage so the
 * next request (or the retry of the original request) picks it up.
 *
 * NOTE: This is the canonical refresher driven by the 401 auto-refresh
 * interceptor in ApiService. A second legacy path exists at
 * `AuthService.refreshToken()` (auth.service.ts) for callers that need the
 * full `LoginResponse` back. Both POST to `/api/auth/refresh` — keep them in
 * sync if the refresh contract ever changes.
 */
export async function performAccessTokenRefreshAgainstBackend(
  baseURL: string,
): Promise<RefreshResult> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_FETCH_TIMEOUT_MS)
  try {
    const response = await fetch(`${baseURL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Fastify rejects POST with `Content-Type: application/json` and empty
      // body (`FST_ERR_CTP_EMPTY_JSON_BODY` → 400). The endpoint expects no
      // payload (refresh token is in the httpOnly cookie), so we send an
      // empty JSON object — backend ignores it.
      body: '{}',
      // Required: refresh token lives in an httpOnly cookie set by the backend.
      credentials: 'include',
      signal: controller.signal,
    })

    if (!response.ok) {
      logger.warn(`[API] Refresh endpoint returned non-OK status: ${response.status}`)
      // 400 (MISSING_REFRESH_TOKEN: no cookie), 401, 403 and 404 mean the server looked at the credential (or
      // found none) and refused it: the session is dead. Only 408, 429, 5xx, network/timeout errors and a broken
      // 200 body say nothing about the credential, so they are not a decision about the session.
      if (REJECTED_REFRESH_STATUSES.has(response.status)) return { kind: 'rejected' }
      const retryAfterSeconds = parseRetryAfterSeconds(response.headers.get('Retry-After'))
      return retryAfterSeconds === undefined
        ? { kind: 'transient' }
        : { kind: 'transient', retryAfterSeconds }
    }

    const body = (await response.json()) as RefreshSuccessResponseBody
    const newAccessToken = body?.data?.tokens?.accessToken

    if (typeof newAccessToken !== 'string' || newAccessToken.length === 0) {
      logger.warn('[API] Refresh endpoint returned malformed body (no accessToken)')
      return { kind: 'rejected' }
    }

    // Persist the new token; the next request (or retry) will read it from storage.
    storageService.setAuthToken(newAccessToken)
    return { kind: 'ok', accessToken: newAccessToken }
  } catch (error) {
    // Network / parse / abort errors: the refresh could not be completed, which is not a refusal.
    logger.warn('[API] Token refresh failed:', error)
    return { kind: 'transient' }
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Clears local auth state without importing authService (would be a circular
 * dep). Mirrors the side effects of `authService.clearAuthData()`.
 *
 * NOTE: cannot remove the in-memory `Authorization` header on the ApiService
 * singleton from here without circular import — caller does that.
 */
export function clearAuthDataLocally(): void {
  storageService.removeAuthToken()
  storageService.removeLocal(STORAGE_KEYS.REFRESH_TOKEN)
  storageService.removeLocal(STORAGE_KEYS.USER_DATA)
}
